import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { okxWs } from "@/lib/ws-okx";

export type WsStatus = "connected" | "connecting" | "disconnected";

interface WsContext {
  useLivePrice: (symbol: string) => number | null;
  status: WsStatus;
}

const Ctx = createContext<WsContext | null>(null);

// Per-symbol refcount and latest price cache shared across all hook instances.
// Stored outside React state so they survive re-renders without causing them.
const priceCache: Map<string, number> = new Map();

export function WsProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<WsStatus>("disconnected");

  useEffect(() => {
    return okxWs.onStatusChange(setStatus);
  }, []);

  const ctx: WsContext = {
    useLivePrice,
    status,
  };
  return <Ctx.Provider value={ctx}>{children}</Ctx.Provider>;
}

export function useWs(): WsContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWs must be used within WsProvider");
  return ctx;
}

/**
 * Subscribe to live OKX ticker price for a symbol.
 * Multiple components using the same symbol share a single WS subscription
 * via the okxWs refcount mechanism.
 */
export function useLivePrice(symbol: string): number | null {
  const [price, setPrice] = useState<number | null>(() => priceCache.get(symbol) ?? null);
  // Keep a stable ref to the symbol so the cleanup closure captures the right value
  const symbolRef = useRef(symbol);
  symbolRef.current = symbol;

  useEffect(() => {
    // Sync initial value from cache in case another component already has it
    const cached = priceCache.get(symbol);
    if (cached !== undefined) setPrice(cached);

    const handler = (p: number) => {
      priceCache.set(symbol, p);
      setPrice(p);
    };

    okxWs.subscribe(symbol, handler);
    return () => {
      okxWs.unsubscribe(symbolRef.current, handler);
    };
  }, [symbol]);

  return price;
}
